import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getActiveBanners,
  getActivePopups,
  isDismissed,
  dismissNotification,
  seedDefaultBannerIfNeeded,
  NOTIFY_BANNER_ID,
  type SiteBanner,
  type SitePopup,
  type BannerStyle,
  type BannerThickness,
} from "@/utils/popupBannerStore";
import NotifyBanner from "@/pages/home/components/NotifyBanner";

// ─── Style helpers ────────────────────────────────────────────────────────────

function bannerColors(style: BannerStyle) {
  switch (style) {
    case "success": return { bg: "bg-green-500", text: "text-white", btn: "bg-white/20 hover:bg-white/30 text-white" };
    case "warning": return { bg: "bg-amber-400", text: "text-amber-900", btn: "bg-amber-900/20 hover:bg-amber-900/30 text-amber-900" };
    case "promo":   return { bg: "bg-pink-500", text: "text-white", btn: "bg-white/20 hover:bg-white/30 text-white" };
    default:        return { bg: "bg-gray-900", text: "text-white", btn: "bg-white/20 hover:bg-white/30 text-white" };
  }
}

function thicknessPadding(thickness: BannerThickness | undefined): string {
  switch (thickness) {
    case "slim":   return "py-1.5 px-4";
    case "tall":   return "py-4 px-4";
    default:       return "py-2.5 px-4"; // normal
  }
}

function popupAccent(style: BannerStyle) {
  switch (style) {
    case "success": return "border-green-400";
    case "warning": return "border-amber-400";
    case "promo":   return "border-pink-400";
    default:        return "border-gray-400";
  }
}

function popupBtnColor(style: BannerStyle) {
  switch (style) {
    case "success": return "bg-green-500 hover:bg-green-600";
    case "warning": return "bg-amber-500 hover:bg-amber-600";
    case "promo":   return "bg-pink-500 hover:bg-pink-600";
    default:        return "bg-gray-800 hover:bg-gray-700";
  }
}

// ─── Banner strip ─────────────────────────────────────────────────────────────

function BannerStrip({ banner, onDismiss }: { banner: SiteBanner; onDismiss: () => void }) {
  const navigate = useNavigate();
  const colors = bannerColors(banner.style);
  const padding = thicknessPadding(banner.thickness);

  const handleCta = () => {
    if (!banner.ctaUrl) return;
    if (banner.ctaUrl.startsWith("http")) {
      window.open(banner.ctaUrl, "_blank", "noopener");
    } else {
      navigate(banner.ctaUrl);
    }
  };

  return (
    <div className={`w-full ${colors.bg} ${colors.text} ${padding} flex items-center justify-center gap-3 text-sm font-medium relative`}>
      <span className="text-center leading-snug">
        {banner.text}
        {banner.subText && (
          <span className="ml-2 opacity-80 text-xs">{banner.subText}</span>
        )}
      </span>
      {banner.ctaLabel && banner.ctaUrl && (
        <button
          onClick={handleCta}
          className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap cursor-pointer transition-colors ${colors.btn}`}
        >
          {banner.ctaLabel}
        </button>
      )}
      {banner.dismissible && (
        <button
          onClick={onDismiss}
          className={`absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full cursor-pointer transition-colors opacity-70 hover:opacity-100 ${colors.btn}`}
          aria-label="Dismiss"
        >
          <i className="ri-close-line text-sm"></i>
        </button>
      )}
    </div>
  );
}

// ─── Popup modal ──────────────────────────────────────────────────────────────

function PopupModal({ popup, onDismiss }: { popup: SitePopup; onDismiss: () => void }) {
  const navigate = useNavigate();
  const accent = popupAccent(popup.style);
  const btnColor = popupBtnColor(popup.style);

  const handleCta = () => {
    if (!popup.ctaUrl) return;
    onDismiss();
    if (popup.ctaUrl.startsWith("http")) {
      window.open(popup.ctaUrl, "_blank", "noopener");
    } else {
      navigate(popup.ctaUrl);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/60 p-4"
      onClick={onDismiss}
    >
      <div
        className={`w-full max-w-md bg-white rounded-3xl border-t-4 ${accent} overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-7">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 leading-tight pr-4">{popup.title}</h2>
            <button
              onClick={onDismiss}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 cursor-pointer flex-shrink-0 transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">{popup.body}</p>
          <div className="flex gap-3">
            {popup.ctaLabel && popup.ctaUrl && (
              <button
                onClick={handleCta}
                className={`flex-1 ${btnColor} text-white font-bold py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors`}
              >
                {popup.ctaLabel}
              </button>
            )}
            <button
              onClick={onDismiss}
              className="flex-1 border border-gray-200 text-gray-500 font-semibold py-2.5 rounded-full cursor-pointer whitespace-nowrap hover:border-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SiteNotifications() {
  const [banners, setBanners] = useState<SiteBanner[]>([]);
  const [activePopup, setActivePopup] = useState<SitePopup | null>(null);
  const [showNotifyBanner, setShowNotifyBanner] = useState(false);

  const refresh = useCallback(() => {
    const activeBanners = getActiveBanners().filter((b) => !isDismissed(b.id));
    setBanners(activeBanners);

    // Check if the special notify banner is enabled
    const notifyBanner = activeBanners.find((b) => b.id === NOTIFY_BANNER_ID);
    setShowNotifyBanner(!!notifyBanner);
  }, []);

  useEffect(() => {
    seedDefaultBannerIfNeeded();
    refresh();

    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === "ma_site_notifications") refresh();
    };
    window.addEventListener("storage", onStorage);
    const t = setTimeout(refresh, 1500);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearTimeout(t);
    };
  }, [refresh]);

  // Handle popups with triggers
  useEffect(() => {
    const popups = getActivePopups().filter((p) => {
      if (p.showOnce && isDismissed(p.id)) return false;
      return true;
    });

    if (popups.length === 0) return;

    const timers: ReturnType<typeof setTimeout>[] = [];

    popups.forEach((popup) => {
      if (popup.trigger === "on-load") {
        const t = setTimeout(() => setActivePopup(popup), 300);
        timers.push(t);
      } else if (popup.trigger === "after-delay") {
        const t = setTimeout(() => setActivePopup(popup), (popup.delaySeconds || 5) * 1000);
        timers.push(t);
      } else if (popup.trigger === "on-exit") {
        const handleMouseLeave = (e: MouseEvent) => {
          if (e.clientY <= 0) {
            setActivePopup(popup);
            document.removeEventListener("mouseleave", handleMouseLeave);
          }
        };
        document.addEventListener("mouseleave", handleMouseLeave);
        return () => document.removeEventListener("mouseleave", handleMouseLeave);
      }
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  const handleDismissBanner = (id: string) => {
    dismissNotification(id);
    setBanners((prev) => prev.filter((b) => b.id !== id));
    if (id === NOTIFY_BANNER_ID) setShowNotifyBanner(false);
  };

  const handleDismissPopup = (popup: SitePopup) => {
    if (popup.showOnce) dismissNotification(popup.id);
    setActivePopup(null);
  };

  // Separate top and bottom banners (excluding the special notify banner which renders as NotifyBanner)
  const regularBanners = banners.filter((b) => b.id !== NOTIFY_BANNER_ID);
  const topBanners = regularBanners.filter((b) => !b.position || b.position === "top");
  const bottomBanners = regularBanners.filter((b) => b.position === "bottom");

  return (
    <>
      {/* Top banners — fixed above navbar */}
      {topBanners.length > 0 && (
        <div className="fixed top-0 left-0 w-full z-[60] flex flex-col">
          {topBanners.map((banner) => (
            <BannerStrip
              key={banner.id}
              banner={banner}
              onDismiss={() => handleDismissBanner(banner.id)}
            />
          ))}
        </div>
      )}

      {/* Bottom banners — fixed at bottom (above NotifyBanner if both exist) */}
      {bottomBanners.length > 0 && (
        <div className={`fixed left-0 w-full z-[59] flex flex-col ${showNotifyBanner ? "bottom-[60px]" : "bottom-0"}`}>
          {bottomBanners.map((banner) => (
            <BannerStrip
              key={banner.id}
              banner={banner}
              onDismiss={() => handleDismissBanner(banner.id)}
            />
          ))}
        </div>
      )}

      {/* Special email-capture notify banner — shown at bottom when enabled */}
      {showNotifyBanner && (
        <NotifyBanner onDismiss={() => handleDismissBanner(NOTIFY_BANNER_ID)} />
      )}

      {/* Active popup */}
      {activePopup && (
        <PopupModal
          popup={activePopup}
          onDismiss={() => handleDismissPopup(activePopup)}
        />
      )}
    </>
  );
}

// Export banner height helper for layout offset
export function useBannerOffset() {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const banners = getActiveBanners().filter((b) => !isDismissed(b.id));
    setOffset(banners.filter((b) => !b.position || b.position === "top").length * 40);
  }, []);
  return offset;
}
